/**
 * MetaApiBillingEstimator — a compact billing calculator for MetaAPI usage.
 *
 * MetaAPI charges by DEPLOYMENT TIME (per-account per-day), not by trades or
 * lot size. This widget helps the trader:
 *   • see the daily burn rate for his currently-deployed accounts,
 *   • compare High Reliability vs Standard side by side,
 *   • plug in his current MetaAPI balance to know how many days it lasts,
 *   • track top-up history and get a red toast when est. days < 3.
 *
 * Rates are hard-coded constants — update if MetaAPI adjusts pricing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Activity, ChevronDown, Info, Rocket, Server, Zap, TrendingUp, Wallet,
  AlertTriangle, PlusCircle, History, Trash2, Download,
} from 'lucide-react';

// Per-account per-day rates — from MetaAPI's published pricing.
const RATES = {
  high: { label_ar: 'High Reliability', label_en: 'High Reliability', usd_per_day: 0.79, color: '#d4af37' },
  std:  { label_ar: 'Standard',         label_en: 'Standard',         usd_per_day: 0.34, color: '#38bdf8' },
};

const LS_HISTORY_KEY = 'mt5_metaapi_balance_history';
const LS_ALERT_KEY   = 'mt5_metaapi_last_low_alert';
const LOW_DAYS_THRESHOLD = 3;

const currency = (v) => `$${Number(v || 0).toFixed(2)}`;

function daysLabel(days, isAr) {
  if (!Number.isFinite(days) || days <= 0) return '—';
  if (days < 1) return isAr ? 'أقل من يوم' : 'less than a day';
  if (days < 30) return isAr ? `~${Math.round(days)} يوم` : `~${Math.round(days)} days`;
  const months = days / 30;
  return isAr ? `~${months.toFixed(1)} شهر` : `~${months.toFixed(1)} months`;
}

// ── LocalStorage helpers ──────────────────────────────────────────
function loadHistory() {
  try {
    const raw = localStorage.getItem(LS_HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function saveHistory(list) {
  try { localStorage.setItem(LS_HISTORY_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

// Compute the estimated CURRENT balance from the most recent recorded top-up,
// minus the estimated consumption since then.
function estimateCurrentBalance(history, dailyRate) {
  if (!history?.length || !dailyRate) return null;
  const last = history[history.length - 1];
  if (!last) return null;
  const then = new Date(last.at).getTime();
  const now = Date.now();
  const daysElapsed = Math.max(0, (now - then) / (1000 * 60 * 60 * 24));
  return Math.max(0, Number(last.balance) - daysElapsed * dailyRate);
}

function PlanCard({ plan, deployedCount, balance, isAr, highlight }) {
  const rate = RATES[plan];
  const daily = deployedCount * rate.usd_per_day;
  const monthly = daily * 30;
  const daysLeft = daily > 0 && balance ? balance / daily : null;

  return (
    <div
      className={`rounded-xl border p-3 transition ${
        highlight
          ? 'border-[#d4af37]/40 bg-gradient-to-br from-[#d4af37]/[0.08] to-transparent shadow-[0_0_16px_rgba(212,175,55,0.15)]'
          : 'border-white/10 bg-white/[0.02]'
      }`}
      data-testid={`billing-plan-${plan}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: `${rate.color}22`, border: `1px solid ${rate.color}55` }}
          >
            {plan === 'high' ? <Rocket className="h-3.5 w-3.5" style={{ color: rate.color }} /> : <Server className="h-3.5 w-3.5" style={{ color: rate.color }} />}
          </div>
          <span className="text-xs font-black text-white" dir="ltr">{rate.label_ar}</span>
        </div>
        {highlight && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#d4af37]/20 text-[#d4af37] font-bold border border-[#d4af37]/30">
            {isAr ? 'الحالي' : 'current'}
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-white/50">{isAr ? 'يومياً' : 'Daily'}</span>
          <span className="font-mono font-bold text-white" dir="ltr">
            {deployedCount} × {currency(rate.usd_per_day)} = <span style={{ color: rate.color }}>{currency(daily)}</span>
          </span>
        </div>
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-white/50">{isAr ? 'شهرياً' : 'Monthly'}</span>
          <span className="font-mono font-bold text-white" dir="ltr">{currency(monthly)}</span>
        </div>
        {daysLeft != null && (
          <div className="flex items-center justify-between text-[11px] pt-1.5 border-t border-white/5">
            <span className="text-white/50">
              {currency(balance)} {isAr ? 'يكفي لـ' : 'lasts'}
            </span>
            <span className="font-mono font-bold" style={{ color: rate.color }} dir="ltr">
              {daysLabel(daysLeft, isAr)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MetaApiBillingEstimator({ deployedCount = 0, totalCount = 0, isAr = true }) {
  const [open, setOpen] = useState(false);
  const [balance, setBalance] = useState('');
  const [history, setHistory] = useState(() => loadHistory());
  const [showHistory, setShowHistory] = useState(false);

  const effectiveCount = deployedCount > 0 ? deployedCount : totalCount;
  const dailyHigh = effectiveCount * RATES.high.usd_per_day;
  const dailyStd  = effectiveCount * RATES.std.usd_per_day;

  // Auto-estimated CURRENT balance from the last recorded top-up.
  const autoBalanceHigh = useMemo(() => estimateCurrentBalance(history, dailyHigh), [history, dailyHigh]);

  // Manual override wins if the user typed a number; otherwise show the auto value.
  const balanceNum = balance ? Number(balance) : autoBalanceHigh;

  // Fire ONE red toast when the auto balance drops below the 3-day cushion.
  // 12-hour cool-down so we don't spam.
  useEffect(() => {
    if (autoBalanceHigh == null || dailyHigh <= 0) return;
    const daysLeft = autoBalanceHigh / dailyHigh;
    if (daysLeft >= LOW_DAYS_THRESHOLD) return;
    try {
      const last = localStorage.getItem(LS_ALERT_KEY);
      const now = Date.now();
      if (last && now - Number(last) < 12 * 60 * 60 * 1000) return;
      localStorage.setItem(LS_ALERT_KEY, String(now));
    } catch { /* ignore */ }
    toast.error(
      isAr
        ? `⚠️ رصيد MetaAPI منخفض! يكفي لـ ${daysLabel(daysLeft, true)} فقط. أعد التعبئة الآن.`
        : `⚠️ MetaAPI balance is critically low — only ${daysLabel(daysLeft, false)} left. Top up now.`,
      { duration: 15000 },
    );
  }, [autoBalanceHigh, dailyHigh, isAr]);

  const recordTopUp = useCallback(() => {
    const amount = Number(balance);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error(isAr ? 'أدخل رقماً صحيحاً' : 'Enter a valid amount');
      return;
    }
    const entry = { at: new Date().toISOString(), balance: amount, note: 'top-up' };
    const next = [...history, entry].slice(-20);
    setHistory(next);
    saveHistory(next);
    setBalance('');
    // Reset the low-balance alert cooldown so a fresh top-up doesn't
    // immediately re-fire the same alert.
    try { localStorage.removeItem(LS_ALERT_KEY); } catch { /* ignore */ }
    toast.success(
      isAr
        ? `تم تسجيل ${currency(amount)} في السجل`
        : `Recorded ${currency(amount)} in history`,
    );
  }, [balance, history, isAr]);

  const removeEntry = (idx) => {
    const next = history.filter((_, i) => i !== idx);
    setHistory(next);
    saveHistory(next);
  };

  const isLow = autoBalanceHigh != null && dailyHigh > 0 && (autoBalanceHigh / dailyHigh) < LOW_DAYS_THRESHOLD;

  return (
    <div
      className={`rounded-2xl border overflow-hidden ${
        isLow ? 'border-red-500/30 bg-red-500/[0.03]' : 'border-white/10 bg-white/[0.02]'
      }`}
      data-testid="metaapi-billing-estimator"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/[0.02] transition text-start"
        data-testid="billing-estimator-toggle"
      >
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center border ${
            isLow
              ? 'bg-red-500/15 border-red-500/40'
              : 'bg-[#d4af37]/15 border-[#d4af37]/30'
          }`}>
            <Wallet className={`h-3.5 w-3.5 ${isLow ? 'text-red-300' : 'text-[#d4af37]'}`} />
          </div>
          <div>
            <div className="text-xs font-black text-white flex items-center gap-2">
              {isAr ? 'حاسبة MetaAPI (تقديرية)' : 'MetaAPI cost estimator'}
              {isLow && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500/25 border border-red-500/50 text-red-300 text-[9px] font-black animate-pulse">
                  <AlertTriangle className="h-2.5 w-2.5" />
                  {isAr ? 'رصيد منخفض' : 'low'}
                </span>
              )}
            </div>
            <div className="text-[10px] text-white/50">
              {effectiveCount > 0 ? (
                <span dir="ltr">
                  {effectiveCount} {isAr ? 'حساب' : 'accounts'} •{' '}
                  <span className="text-[#d4af37]">{currency(dailyHigh)}/day (HR)</span>{' '}
                  <span className="text-white/30">|</span>{' '}
                  <span className="text-[#38bdf8]">{currency(dailyStd)}/day (Std)</span>
                  {autoBalanceHigh != null && (
                    <>
                      {' '}<span className="text-white/30">|</span>{' '}
                      <span className={isLow ? 'text-red-300 font-bold' : 'text-white/70'}>
                        {isAr ? 'رصيد مقدَّر:' : 'est balance:'} {currency(autoBalanceHigh)}
                      </span>
                    </>
                  )}
                </span>
              ) : (
                isAr ? 'اضغط للتفاصيل' : 'Click for details'
              )}
            </div>
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 text-white/60 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4 pt-2 space-y-3 border-t border-white/5">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-white/60">
                  <Activity className="inline h-3 w-3 me-1" />
                  {isAr
                    ? `المُشغَّلة الآن: ${deployedCount} / ${totalCount} حساب`
                    : `Deployed: ${deployedCount} / ${totalCount} accounts`}
                </span>
                <span className="text-white/40 text-[10px]">
                  {isAr ? '(الأسعار تقديرية)' : '(estimated)'}
                </span>
              </div>

              {/* Balance input + record top-up */}
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-[11px] text-white/60 font-bold shrink-0">
                  <TrendingUp className="inline h-3 w-3 me-1" />
                  {isAr ? 'رصيد MetaAPI:' : 'Balance:'}
                </label>
                <div className="relative flex-1 min-w-[140px]">
                  <span className={`absolute top-1/2 -translate-y-1/2 text-white/40 text-xs font-mono ${isAr ? 'right-2' : 'left-2'}`}>
                    $
                  </span>
                  <input
                    type="number"
                    value={balance}
                    onChange={(e) => setBalance(e.target.value)}
                    placeholder={isAr ? 'مثال: 120' : 'e.g. 120'}
                    className={`w-full bg-black/40 border border-white/10 rounded-lg py-1.5 text-xs font-mono text-white placeholder-white/30 focus:outline-none focus:border-[#d4af37]/40 ${isAr ? 'pr-6 pl-3' : 'pl-6 pr-3'}`}
                    data-testid="billing-balance-input"
                    dir="ltr"
                  />
                </div>
                {[70, 100, 120, 150, 300].map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setBalance(String(amt))}
                    className="px-2 py-1 rounded-md bg-white/[0.03] border border-white/10 hover:bg-white/[0.08] text-[10px] font-bold text-white/70"
                    data-testid={`billing-quick-${amt}`}
                  >
                    ${amt}
                  </button>
                ))}
                <button
                  onClick={recordTopUp}
                  disabled={!balance}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[11px] font-bold hover:bg-emerald-500/25 disabled:opacity-40"
                  data-testid="billing-record-topup"
                  title={isAr ? 'حفظ في السجل' : 'Save to history'}
                >
                  <PlusCircle className="h-3 w-3" />
                  {isAr ? 'سجّل التوب-أب' : 'Record top-up'}
                </button>
              </div>

              {/* Side-by-side plans */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <PlanCard plan="high" deployedCount={effectiveCount} balance={balanceNum} isAr={isAr} highlight />
                <PlanCard plan="std"  deployedCount={effectiveCount} balance={balanceNum} isAr={isAr} />
              </div>

              {/* Balance history */}
              {history.length > 0 && (
                <div className="rounded-lg border border-white/10 bg-black/20">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setShowHistory(!showHistory)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowHistory(!showHistory); }}
                    className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-white/70 hover:bg-white/[0.03] cursor-pointer select-none"
                    data-testid="billing-history-toggle"
                  >
                    <span className="flex items-center gap-1.5">
                      <History className="h-3 w-3" />
                      {isAr ? `سجل التوب-أب (${history.length})` : `Top-up history (${history.length})`}
                    </span>
                    <span className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Client-side CSV export (data lives in localStorage — no backend).
                          const header = ['at_utc', 'at_local', 'amount_usd', 'notes'];
                          const rows = [...history]
                            .sort((a, b) => new Date(a.at) - new Date(b.at))
                            .map((h) => [
                              h.at,
                              new Date(h.at).toLocaleString('en-CA'),
                              Number(h.balance || 0).toFixed(2),
                              (h.note || '').replace(/[\r\n,]/g, ' '),
                            ]);
                          const csv = '\ufeff'
                            + header.join(',') + '\n'
                            + rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
                          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `metaapi-topups-${new Date().toISOString().slice(0, 10)}.csv`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                          toast.success(isAr ? `✅ تمّ تصدير ${history.length} عمليّة شحن` : `✅ Exported ${history.length} top-ups`);
                        }}
                        className="p-1 rounded text-white/40 hover:text-emerald-300 hover:bg-emerald-500/10 transition"
                        title={isAr ? 'تصدير CSV' : 'Export CSV'}
                        data-testid="billing-history-export"
                      >
                        <Download className="h-3 w-3" />
                      </button>
                      <ChevronDown className={`h-3 w-3 transition ${showHistory ? 'rotate-180' : ''}`} />
                    </span>
                  </div>
                  {showHistory && (
                    <div className="px-3 pb-2 space-y-1 max-h-40 overflow-y-auto" data-testid="billing-history-list">
                      {[...history].reverse().map((h, i) => {
                        const realIdx = history.length - 1 - i;
                        return (
                          <div
                            key={h.at}
                            className="flex items-center justify-between text-[10px] font-mono px-2 py-1 rounded bg-white/[0.02]"
                            data-testid={`billing-history-entry-${realIdx}`}
                            dir="ltr"
                          >
                            <span className="text-white/50">
                              {new Date(h.at).toLocaleString(isAr ? 'ar-AE' : 'en')}
                            </span>
                            <span className="text-emerald-300 font-bold">+${Number(h.balance).toFixed(2)}</span>
                            <button
                              onClick={() => removeEntry(realIdx)}
                              className="p-1 rounded text-white/40 hover:text-red-400 hover:bg-red-500/10"
                              title={isAr ? 'حذف' : 'Remove'}
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Tip */}
              <div className="flex items-start gap-2 rounded-lg bg-blue-500/[0.06] border border-blue-500/20 px-2.5 py-1.5 text-[10px] text-blue-200/90">
                <Info className="h-3 w-3 shrink-0 mt-0.5" />
                <span>
                  {isAr ? (
                    <>
                      <strong className="text-blue-300">كيف يحسب:</strong>{' '}
                      MetaAPI يخصم مقابل <strong>وقت التشغيل فقط</strong> (وليس عدد الصفقات/اللوت).
                      حسابات <span className="text-white/70">Undeployed</span> لا تُخصم منها.
                      إذا أنقصت ساعات التشغيل من 24 إلى 12 → التكلفة تنخفض للنصف تقريباً.
                    </>
                  ) : (
                    <>
                      <strong className="text-blue-300">How it charges:</strong>{' '}
                      MetaAPI bills for <strong>deployment time only</strong> (not trades / lot size).
                      Undeployed accounts don&apos;t consume balance. Halving deployment hours
                      roughly halves the daily cost.
                    </>
                  )}
                </span>
              </div>

              <a
                href="https://app.metaapi.cloud/billing"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37] text-[11px] font-bold hover:bg-[#d4af37]/25 transition"
                data-testid="billing-open-metaapi"
              >
                <Zap className="h-3.5 w-3.5" />
                {isAr ? 'افتح صفحة الفوترة على MetaAPI' : 'Open MetaAPI billing page'}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
